import {
  AgGridEvent,
  CellClassParams,
  CellMouseDownEvent,
  CellMouseOverEvent,
  CellStyle,
  ColDef,
  ColumnApi,
  Events,
  FilterChangedEvent,
  GridApi,
  GridOptions,
  GridReadyEvent,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  ISetFilterParams,
  Module,
  RowClassParams,
  RowClickedEvent,
  RowDoubleClickedEvent,
  RowNode,
} from '@ag-grid-community/core'
import { FiltersToolPanelModule } from '@ag-grid-enterprise/filter-tool-panel'
import { RangeSelectionModule } from '@ag-grid-enterprise/range-selection'
import { ServerSideRowModelModule } from '@ag-grid-enterprise/server-side-row-model'
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core'
import { FormControl } from '@angular/forms'
import {
  disableChangeDetectionOnScroll,
  enableHeliosCopyBehavior,
  enableHeliosGridDensityLevel,
  enableHeliosMultiRowDragSelection,
  generateHeliosAgGridSidebarFromScratch,
  generateMdiIcon,
  HeliosColumnTypes,
  heliosDefaultColWidth,
  heliosDefaultToolPanels,
  heliosMultiRowSelectionConfig,
  heliosServerSideRowConfig,
  NumberFilterOptionsWithAbsolute,
  NumberFilterOptionsWithoutEquals,
  setFilter,
  TextFilterOptionsWithContainsIn,
} from '@helios/ag-grid-config'
import {
  ClearwaterTransaction,
  ExternalTransaction,
  ExternalTransactionStatus,
  formatDates,
} from '@helios/common-api'
import {
  CreateTransactionsModalService,
  TransactionModalMode,
} from '@helios/create-transactions-modal'
import { FilterModalService, FilterModel } from '@helios/filter-modal'
import {
  ErrorStatusRenderer,
  errorStatusRendererKey,
  GenericTxSummary,
  getColDefs,
  getGridState,
  HighlightingService,
  mergeBaseColDefsWithColumnState,
  TransactionsStatusBarModule,
  transactionsStatusBarPanel,
  updateColDefs,
} from '@helios/generic-components'
import {
  ExternalTransactionsGridHotkeysType,
  HighlightRowsInExtTransactions,
  HotkeyDestroyFn,
  HotkeysService,
} from '@helios/hotkeys'
import {
  convertFilters,
  countFilters,
  Filter,
  genericHeliosRequest,
  handleColumnVisibilityChange,
  HeliosServerSideRequest,
  toHeliosRequest,
} from '@helios/server-side-util'
import {
  ExternalTxGridContext,
  GridState,
  RootState,
  selectActiveSavedViewForGrid,
  selectActiveSession,
  selectFocusedAssetId,
  selectIsHplAccount,
  selectSavedViewExistsForGridKey,
  selectUserPolicy,
  SessionCacheStatus,
  setFocusedAssetId,
  userHasCommentaryAccess,
} from '@helios/state-management'
import {
  createFilterGroupsToolPanel,
  DensityLevel,
  FilterGroupsToolPanelParams,
  UserPreferencesService,
  WorkspaceGridSavedViewService,
  WorkspaceUpdateEventObject,
} from '@helios/user-preferences'
import {
  compareSets,
  downloadExcelFile,
  FilterTypeForQuickFilterOfLength,
  isNullOrEmpty,
} from '@helios/utils'
import {
  AssetIdListValidator,
  AssetIdValidator,
  IntegerIdValidator,
} from '@helios/validators'
import {
  CommentaryCountsService,
  CommentaryLiveAccountsService,
  CommentaryEntityRef,
  CommentaryEntitySearchService,
  CommentaryFollowService,
  CommentaryGridSelectionService,
  CommentCountCellRenderer,
  commentCountRendererKey,
  wireCommentaryGrid,
  wireCommentaryCounts,
  refilterOnThreadChanges,
  activeSessionAccountIds,
  getCommentaryColumnIfAccessible,
  getCommentaryContextMenuItems,
  COMMENTARY_COLUMN_ID,
  externalTransactionCounts,
} from '@helios/comments'
import { Store } from '@ngrx/store'
import { AgGridApi, NfColumnTypes } from '@northfork/ag-grid'
import { AgGridState } from '@northfork/ag-grid-angular'
import { ToastService } from '@northfork/ng-basics'
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { refreshSetValuesForAllSetFilterColumns } from 'libs/filter-modal/src/lib/utils/column-filter-utils'
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { createPanelSettingsToolPanel } from 'libs/generic-components/src/lib/panel-settings-tool-panel/utils/panel-settings-tool-panel-sidebar-utils'
import { debounce } from 'lodash-es'
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs'
import {
  debounceTime,
  distinctUntilChanged,
  map,
  shareReplay,
  startWith,
  take,
  takeUntil,
  takeWhile,
} from 'rxjs/operators'
import { ExternalTransactionsKey } from '../../panel-key'
import {
  ExportExcelRequest,
  ExternalTransactionSetValues,
  ExternalTransactionStatusKey,
  externalTransactionField as field,
  TransactionAvailableRestrictedColumns,
} from '../models/external-transaction'
import { ExternalTransactionsTaskService } from '../services/external-transactions-task.service'
import { ExternalTransactionsService } from '../services/external-transactions.service'
import { getExternalTransactionsContextMenuItems } from '../utils/external-transactions-context-menu-utils'
import { externalTransactionCommentary } from './external-transactions-commentary'

/**
 * DO NOT CHANGE. Saved Views and Filter Groups depend on this key. Changing will result in users losing their saved
 * preferences for this grid.
 */
export const ExternalTransactionsMainGridKey = `${ExternalTransactionsKey}-MainGrid`

const STATUS_FILTER_DEFAULTS = ['HELD', 'UNMATCHED']

@Component({
  selector: 'hs-external-transactions-grid',
  templateUrl: './external-transactions-grid.component.html',
  styleUrls: ['./external-transactions-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [HighlightingService, FilterModalService],
})
export class ExternalTransactionsGridComponent implements OnDestroy, OnInit {
  private readonly destroySubject$ = new Subject<void>()
  filterCount = 0
  newSelectionRefresh = false
  directMatchedTransactions: Set<RowNode> = new Set()
  latestDragStartingRow?: number

  @Input()
  set securityIdFilter(securityId: number | null | undefined) {
    let filter: unknown
    if (securityId == null) {
      filter = {}
    } else {
      filter = {
        filterType: 'number',
        type: 'in',
        filter: String(securityId),
      }
    }

    this.setFilter('securityId', filter)
  }

  @Input()
  set currencyFilter(currency: string | null | undefined) {
    let filter: unknown
    if (currency == null) {
      filter = {}
    } else {
      filter = {
        filterType: 'set',
        values: [currency],
      }
    }

    this.setFilter('currencyCode', filter)
  }

  @Input()
  set rowsUpdated(extTxIds: string[] | null) {
    if (extTxIds != null && extTxIds.length > 0) {
      this.refreshServerSideStore()
    }
  }

  @Input()
  set focusedExtTranId(extTranId: number | null | undefined) {
    if (!extTranId) {
      return
    }

    this.gridApi?.api.setFilterModel(null)

    this.setFilter('extTranId', {
      filterType: 'number',
      type: 'equals',
      filter: extTranId + '',
    })
  }

  @Input()
  set focusedExtTranIds(extTranIds: number[] | null | undefined) {
    this.gridApi?.api.setFilterModel(null)

    this.setFilter('extTranId', {
      filterType: 'number',
      type: 'in',
      filter: extTranIds?.join(','),
    })
    this.setStatusFilter(new Set())
  }

  /**
   * @internal
   */
  userIsSelectingAllRows = false

  /**
   * @internal
   */
  readonly refreshServerSideStore = debounce(
    () => {
      this.resetSetValues()
      this.gridApi?.api.refreshServerSideStore({ purge: false })
    },
    250,
    { maxWait: 1000 },
  )

  selectAllExcludedTranRows: Set<number> = new Set()
  transactionsDataSummary = new BehaviorSubject<GenericTxSummary>({})
  selectAllTransactionsData: ExternalTransaction[] = []
  availableRestrictedColumns = this.getAvailableRestrictedColumns()

  readonly handleSelectionChanged = debounce(
    async ({ api }: AgGridEvent) => {
      if (api.getSelectedRows().length === 0) {
        this.clearSelectAllRows()
        api.refreshCells()
      }

      if (this.userIsSelectingAllRows) {
        await this.updateSelectAllData(api)
      } else {
        this.transactionsDataSummary.next({})
        this.selectAllTransactionsData = []
        this.externalTransactionsSelected.emit(api.getSelectedRows())
      }

      if (this.directMatchedTransactions.size > 0) {
        this.newSelectionRefresh = true
        api.redrawRows({
          rowNodes: Array.from(this.directMatchedTransactions.keys()),
        })
        this.directMatchedTransactions = new Set()
        this.newSelectionRefresh = false
      }
    },
    100,
    { leading: false },
  )

  @Input()
  set filteredMatchedExtTranIds(
    extTranIds: Array<number | undefined> | undefined | null,
  ) {
    this.setFilter('extTranId', {
      filterType: 'number',
      type: 'in',
      filter: extTranIds?.join(','),
    })
    this.setStatusFilter(new Set([ExternalTransactionStatus.MATCHED]))
  }

  @Input()
  set selectedCwTrans(cwanTrans: ClearwaterTransaction[]) {
    this.cwTransSelected = cwanTrans
    if (this.gridApi) this.gridApi.api.redrawRows()
  }

  _cacheLoaded = false
  private readonly cacheLoaded$ = new BehaviorSubject<boolean>(false)
  /** The session this grid was created for; a session switch replaces the grid. */
  private sessionAccountIds?: readonly number[]
  _cacheLoadStatus: SessionCacheStatus | null = null
  @Input()
  set cacheLoadStatus(value: SessionCacheStatus | null) {
    if (this._cacheLoadStatus === value) return
    if (this.cacheLoaded) {
      if (value === SessionCacheStatus.DONE) {
        this.refreshServerSideStore()
      }
      return
    }
    this._cacheLoadStatus = value
    this._cacheLoaded = this.cacheLoaded
    this.cacheLoaded$.next(this._cacheLoaded)

    this.resetSetValues()

    this.getColDefs(this.cacheLoaded).then(colDefs => {
      updateColDefs(colDefs, this.gridApi)
    })
  }

  get cacheLoaded() {
    return (
      this._cacheLoadStatus === SessionCacheStatus.DONE ||
      this._cacheLoadStatus === SessionCacheStatus.PARTIAL
    )
  }

  @Output() externalTransactionsSelected = new EventEmitter<
    ExternalTransaction[]
  >()

  externalTransactionSetValues$: Observable<ExternalTransactionSetValues> =
    this.getExternalTransactionSetValues()

  readonly gridModules: Module[] = [
    FiltersToolPanelModule,
    ServerSideRowModelModule,
    RangeSelectionModule,
    TransactionsStatusBarModule,
  ]

  /** Table Properties Forms and Attributes Start*/

  //Filters
  sortByAbsoluteValueToggle = new FormControl(false)
  clearFiltersOnAccountLoadToggle = new FormControl(true)

  /** @internal */
  appliedExternalFilters: ExternalTxGridContext = {
    sortByAbsolute: false,
  }

  //Filter Update Apply Button
  applyExternalFiltersButtonEnabled = false

  //Title
  propertiesTitle: string[] = []

  cwTransSelected: ClearwaterTransaction[] = []

  /** Table Properties Forms and Attributes End */

  /**
   * @internal
   */
  get filterGroupsToolPanelParams(): FilterGroupsToolPanelParams<unknown> {
    return {
      gridKey: ExternalTransactionsMainGridKey,
      getExternalFilters: () => ({}),
      setExternalFilters: () => undefined,
      cacheLoaded: this.cacheLoaded$,
    }
  }
  private readonly hotkeyDestroyStack: HotkeyDestroyFn[] = []
  applyHPL: Promise<boolean> = this.store
    .select(selectIsHplAccount)
    .pipe(take(1))
    .toPromise()

  /** @internal */
  gridOptions$ = this.createGridOptions()
  /** @internal */
  gridApi?: AgGridApi
  quickSearchAssetIds = new FormControl('', AssetIdListValidator)
  includeUnmatchedFormControl = new FormControl(false)
  includeHeldFormControl = new FormControl(false)
  includeIgnoredFormControl = new FormControl(false)
  includeMatchedFormControl = new FormControl(false)

  statusFilterSet$?: Observable<Set<string>> = combineLatest([
    this.includeMatchedFormControl.valueChanges.pipe(startWith(false)),
    this.includeUnmatchedFormControl.valueChanges.pipe(startWith(false)),
    this.includeIgnoredFormControl.valueChanges.pipe(startWith(false)),
    this.includeHeldFormControl.valueChanges.pipe(startWith(false)),
  ]).pipe(
    map(values => {
      const [includeMatched, includeUnmatched, includeIgnored, includeHeld] =
        values
      const nextSet = new Set<string>()
      if (includeMatched) {
        nextSet.add('MATCHED')
      }
      if (includeUnmatched) {
        nextSet.add('UNMATCHED')
      }
      if (includeIgnored) {
        nextSet.add('IGNORED')
      }
      if (includeHeld) {
        nextSet.add('HELD')
      }
      return nextSet
    }),
  )

  private gridState?: GridState

  resetAllFiltersEnabled = false
  isAltKeyPressed = false
  hasCopilotPermissions = false
  hasSecurityMappingPermissions = false
  hasExternalFileViewerPermissions = false
  hasAccessCommentary = false
  sessionBasisName?: string

  constructor(
    private readonly transactionsService: ExternalTransactionsService,
    private readonly taskService: ExternalTransactionsTaskService,
    private readonly createTransactionsModalService: CreateTransactionsModalService,
    private readonly store: Store<RootState>,
    private readonly savedViewService: WorkspaceGridSavedViewService,
    private readonly preferences: UserPreferencesService,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly hotkeysService: HotkeysService<ExternalTransactionsGridHotkeysType>,
    private readonly toastService: ToastService,
    // @internal
    public highlightService: HighlightingService,
    // @internal
    public filterModalService: FilterModalService,
    private readonly commentaryCountsService: CommentaryCountsService,
    private readonly commentaryLiveAccounts: CommentaryLiveAccountsService,
    private readonly commentaryFollowService: CommentaryFollowService,
    private readonly entitySearchService: CommentaryEntitySearchService,
    private readonly gridSelectionService: CommentaryGridSelectionService,
  ) {}

  ngOnInit(): void {
    this.filterModalService.isClearFiltersEnabled = false
    this.filterModalService.isResetFiltersEnabled = true
    this.filterModalService.setValidators({
      accountId: IntegerIdValidator,
      secIdentifier: AssetIdValidator,
    })

    this.store
      .select(selectActiveSession)
      .pipe(take(1))
      .subscribe(session => {
        this.sessionBasisName = session?.auxiliaryInfo?.BASIS_NAME
        this.sessionAccountIds = session?.loadedAccountIds
      })

    this.store
      .select(selectUserPolicy)
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(policy => {
        this.hasCopilotPermissions = policy?.accessCopilot ?? false
        this.hasSecurityMappingPermissions =
          policy?.accessSecurityMappingPanel ?? false
        this.hasExternalFileViewerPermissions =
          policy?.accessExternalFileViewerPanel ?? false
      })

    this.store
      .select(selectUserPolicy)
      .pipe(
        takeUntil(this.destroySubject$),
        map(policy => userHasCommentaryAccess(policy)),
        // keep listening only until access is first granted, then complete
        takeWhile(access => !access, true),
      )
      .subscribe(access => {
        this.hasAccessCommentary = access
        if (access) {
          this.wireCommentary()
        }
      })

    this.store
      .select(selectFocusedAssetId)
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(assetId => {
        if (assetId && !this.isAltKeyPressed) {
          this.quickSearchAssetIds.setValue(assetId)
          this.updateQuickSearchAssetIds()
        } else {
          this.isAltKeyPressed = false
        }
      })
    this.sortByAbsoluteValueToggle.valueChanges.subscribe(() => {
      this.updateGridConfig()
      this.applyExternalFilters()
    })
    this.clearFiltersOnAccountLoadToggle.valueChanges.subscribe(() => {
      this.updateGridConfig()
    })
    this.initQuickFilters()
    this.registerHotkeys()
  }

  ngOnDestroy() {
    this.unregisterHotkeys()
    this.destroySubject$.next()
    this.destroySubject$.complete()
    this.gridApi?.api?.destroy()
    this.filterModalService.destroy()
  }

  private wireCommentary() {
    wireCommentaryGrid({
      follow: this.commentaryFollowService,
      search: this.entitySearchService,
      selection: this.gridSelectionService,
      panelKey: ExternalTransactionsKey,
      entityType: 'externalTransaction',
      idColId: 'extTranId',
      getApi: () => this.gridApi?.api,
      hasAccess: () => this.hasAccessCommentary,
      destroy$: this.destroySubject$,
      loadedRefs: () => this.collectLoadedTransactionRefs(),
      selectedRefs: () => this.collectSelectedTransactionRefs(),
      idFilter: id => ({ filterType: 'number', type: 'in', filter: id }),
      toast: this.toastService,
      sessionAccountIds: () => this.sessionAccountIds,
      // The default status filter hides matched and ignored transactions, and
      // clearing it makes the status checkboxes write an empty one back.
      revealAlongside: () => ({
        status: {
          filterType: 'set',
          values: Object.values(ExternalTransactionStatus),
        },
      }),
    })
  }

  private collectLoadedTransactionRefs(): CommentaryEntityRef[] {
    const refs: CommentaryEntityRef[] = []
    this.gridApi?.api.forEachNode(node => {
      const id = (node.data as ExternalTransaction)?.extTranId
      if (id != null) refs.push({ type: 'externalTransaction', id: String(id) })
    })
    return refs
  }

  private collectSelectedTransactionRefs(): CommentaryEntityRef[] {
    return (this.gridApi?.api.getSelectedNodes() ?? [])
      .map(node => (node.data as ExternalTransaction)?.extTranId)
      .filter((id): id is number => id != null)
      .map(id => ({ type: 'externalTransaction' as const, id: String(id) }))
  }

  initQuickFilters() {
    this.quickSearchAssetIds.valueChanges
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        takeUntil(this.destroySubject$),
      )
      .subscribe(() => this.updateQuickSearchAssetIds(true))
  }

  /**
   * Updates the attribute that determines whether or not the button to update the filters in the table properties
   * section is enabled. It checks if the values of the forms are different than the applied filters. If not, we do not
   * want the user to click this button and trigger a reload of the data.
   */
  isApplyExternalFiltersButtonEnabled() {
    this.applyExternalFiltersButtonEnabled =
      this.appliedExternalFilters?.sortByAbsolute !==
      this.sortByAbsoluteValueToggle.value
  }

  applyExternalFilters() {
    this.appliedExternalFilters = {
      sortByAbsolute: this.sortByAbsoluteValueToggle.value,
    }
    this.applyExternalFiltersButtonEnabled = false
    this.gridApi?.api.onFilterChanged()
  }

  //#region GridConfig
  /*@internal*/
  async handleRowClicked(clickedEvent: RowClickedEvent) {
    const keyEvent = clickedEvent.event as PointerEvent

    if (keyEvent == null) {
      return
    }

    if (keyEvent.ctrlKey) {
      if (this.userIsSelectingAllRows) {
        this.toggleSelectAllExcludedTranRows(clickedEvent.data.extTranId)
        await this.updateSelectAllData(clickedEvent.api)
      }
    } else {
      this.clearSelectAllRows()
    }

    if (keyEvent.altKey) {
      this.isAltKeyPressed = true
      this.store.dispatch(setFocusedAssetId(clickedEvent.data.secIdentifier))
    }
  }

  handleOnCellMouseOver(mouseOverEvent: CellMouseOverEvent) {
    if (UserPreferencesService.getUseOldGridMultiRow()) {
      this.handleOnCellMouseOverOld(mouseOverEvent)
      return
    }
    const mouseEvent = mouseOverEvent.event as MouseEvent

    if (mouseEvent.buttons === 1) {
      if (!mouseEvent.ctrlKey) {
        this.clearSelectAllRows()
      } else {
        if (this.userIsSelectingAllRows) {
          if (this.latestDragStartingRow != undefined) {
            this.toggleSelectAllExcludedTranRows(this.latestDragStartingRow)
            this.latestDragStartingRow = undefined
          }
          this.toggleSelectAllExcludedTranRows(mouseOverEvent.data.extTranId)
        }
      }
    }
  }

  handleOnCellMouseOverOld(mouseOverEvent: CellMouseOverEvent) {
    const mouseEvent = mouseOverEvent.event as MouseEvent

    if (mouseEvent.buttons === 1) {
      this.clearSelectAllRows()
    }
  }
  handleOnCellMouseDown(mouseDownEvent: CellMouseDownEvent) {
    if (UserPreferencesService.getUseOldGridMultiRow()) return
    const mouseEvent = mouseDownEvent.event as MouseEvent
    if (mouseEvent.ctrlKey && this.userIsSelectingAllRows) {
      this.latestDragStartingRow = mouseDownEvent.data.extTranId
    }
  }

  clearSelectAllRows() {
    this.selectAllExcludedTranRows.clear()
    this.transactionsDataSummary.next({})
    this.selectAllTransactionsData = []
    this.userIsSelectingAllRows = false
  }

  private async createGridOptions(): Promise<GridOptions> {
    return {
      ...heliosServerSideRowConfig,
      ...heliosMultiRowSelectionConfig,
      cacheBlockSize: 1000,
      columnDefs: await this.createBaseColumnDefs(),
      context: {
        isSelectAllRowsEnabled: () => this.userIsSelectingAllRows,
        summaryData: () => this.transactionsDataSummary.value,
        selectAllTransactionsData: () => this.selectAllTransactionsData,
      },
      components: {
        [errorStatusRendererKey]: ErrorStatusRenderer,
        [commentCountRendererKey]: CommentCountCellRenderer,
      },
      defaultColDef: {
        cellStyle: (params: CellClassParams) =>
          this.highlightService.getCellStyle(
            params?.node?.id ?? '',
            params?.colDef?.field ?? '',
          ) as CellStyle,
        cellRenderer: undefined,
        filterParams: {
          buttons: ['apply', 'reset'],
        },
        tooltipValueGetter: params => params.valueFormatted ?? params.value,
        width: heliosDefaultColWidth.Default,
      },
      sideBar: generateHeliosAgGridSidebarFromScratch([
        createPanelSettingsToolPanel({
          controls: [
            {
              label: 'Sort by Absolute Value',
              formControl: this.sortByAbsoluteValueToggle,
            },
            {
              label: 'Clear Filters on Load',
              formControl: this.clearFiltersOnAccountLoadToggle,
            },
          ],
        }),
        ...heliosDefaultToolPanels,
        createFilterGroupsToolPanel<unknown>(this.filterGroupsToolPanelParams),
      ]),
      getRowNodeId: ({ extTranId }: ExternalTransaction) =>
        extTranId.toString(),
      serverSideDatasource: this.createDatasource(),
      getContextMenuItems: params => [
        ...getExternalTransactionsContextMenuItems(
          params,
          this.cwTransSelected,
          this.taskService,
          this.highlightService,
          this.hasCopilotPermissions,
          this.hasSecurityMappingPermissions,
          this.hasExternalFileViewerPermissions,
          this.sessionBasisName ? true : false,
        ),
        ...getCommentaryContextMenuItems(
          this.store,
          this.hasAccessCommentary,
          externalTransactionCommentary,
          params.node?.data,
        ),
      ],
      onRowDoubleClicked: event => this.onRowDoubleClicked(event),
      statusBar: transactionsStatusBarPanel,
      getRowClass: params => this.deduceRowClass(params),
      onGridReady: event => this.onGridReady(event),
      onRowClicked: event => this.handleRowClicked(event),
      onCellMouseOver: event => this.handleOnCellMouseOver(event),
      onCellMouseDown: event => this.handleOnCellMouseDown(event),
      onRowSelected: event => this.handleSelectionChanged(event),
      onColumnVisible: params =>
        handleColumnVisibilityChange(params, 'ExternalTransactions'),
      onFilterChanged: params => this.onFilterChanged(params),
      rowClassRules: {
        'row-held': params =>
          params.data?.status === ExternalTransactionStatus.HELD,

        'font-italic LIB-transaction': params =>
          this.isLIBTransaction(params.data),
      },
      icons: {
        panelSettings: generateMdiIcon('mdi-cog'),
      },
      ...disableChangeDetectionOnScroll(this.changeDetector),
    }
  }

  private async getColDefs(loaded: boolean): Promise<ColDef[]> {
    return getColDefs(loaded, await this.createBaseColumnDefs(), this.gridApi)
  }

  /**
   * @internal
   */
  async createBaseColumnDefs(): Promise<ColDef[]> {
    const hplColumnType = (await this.applyHPL)
      ? HeliosColumnTypes.HPLNumber
      : NfColumnTypes.Number

    const baseColDefs: ColDef[] = [
      ...(await getCommentaryColumnIfAccessible(
        this.store,
        externalTransactionCommentary,
        () => this.hasAccessCommentary,
        this.commentaryCountsService,
        externalTransactionCounts,
      )),
      {
        ...this.getSetValuesFilterParams('predictedAction'),
        headerName: 'Predicted Action',
        sortable: true,
        field: field('predictedActionProbability'),
        valueGetter: params => {
          let action = ''
          let probability = ''
          if (
            params.data.predictedActionProbability &&
            params.data.predictedAction
          ) {
            probability = `${Math.round(
              params.data.predictedActionProbability * 100,
            )}%`
            action = params.data.predictedAction
          }
          return `${probability} ${action}`
        },
      },
      {
        ...this.getSetValuesFilterParams('status'),
        headerName: 'Status',
        field: field('status'),
        sortable: true,
      },
      {
        headerName: 'Asset ID',
        field: field('secIdentifier'),
        sortable: true,
        filterParams: {
          filterOptions: TextFilterOptionsWithContainsIn,
        },
      },
      {
        field: field('cwanIdentifier'),
        headerName: 'Asset Identifier',
        initialHide: false,
        sortable: true,
        width: 120,
        filterParams: {
          filterOptions: TextFilterOptionsWithContainsIn,
        },
      },
      {
        field: field('secCusip'),
        headerName: 'Sec Cusip',
        hide: true,
        filterParams: {
          filterOptions: TextFilterOptionsWithContainsIn,
        },
      },
      {
        field: field('secIsin'),
        headerName: 'Sec Isin',
        hide: true,
        filterParams: {
          filterOptions: TextFilterOptionsWithContainsIn,
        },
      },
      {
        headerName: 'Sec Figi',
        field: field('secFigi'),
        hide: true,
        filterParams: {
          filterOptions: TextFilterOptionsWithContainsIn,
        },
      },
      {
        headerName: 'Sec Sedol',
        field: field('secSedol'),
        hide: true,
        filterParams: {
          filterOptions: TextFilterOptionsWithContainsIn,
        },
      },
      {
        ...this.getSetValuesFilterParams('tranTypeCodes'),
        headerName: 'Tran Type Code',
        field: field('tranTypeCode'),
        sortable: true,
      },
      {
        headerName: 'Trade Date',
        field: field('tradeDate'),
        type: NfColumnTypes.Date,
        sortable: true,
        valueFormatter: params => {
          return formatDates(params.value, 'yyyy-MM-dd', 'MM/dd/yy')
        },
        tooltipValueGetter: params => {
          return formatDates(params.value, 'yyyy-MM-dd', 'MM/dd/yyyy')
        },
      },
      {
        headerName: 'Settle Date',
        field: field('settleDate'),
        type: NfColumnTypes.Date,
        sortable: true,
        valueFormatter: params => {
          return formatDates(params.value, 'yyyy-MM-dd', 'MM/dd/yy')
        },
        tooltipValueGetter: params => {
          return formatDates(params.value, 'yyyy-MM-dd', 'MM/dd/yyyy')
        },
      },
      {
        headerName: 'Post Date',
        field: field('postDate'),
        type: NfColumnTypes.Date,
        sortable: true,
        valueFormatter: params => {
          return formatDates(params.value, 'yyyy-MM-dd', 'MM/dd/yy')
        },
        tooltipValueGetter: params => {
          return formatDates(params.value, 'yyyy-MM-dd', 'MM/dd/yyyy')
        },
      },
      {
        headerName: 'Amount',
        field: field('amount'),
        type: hplColumnType,
        sortable: true,
        filterParams: {
          filterOptions: NumberFilterOptionsWithAbsolute,
        },
      },
      {
        headerName: 'Price',
        field: field('price'),
        type: NfColumnTypes.Number,
        sortable: true,
        filterParams: {
          filterOptions: NumberFilterOptionsWithAbsolute,
        },
      },
      {
        headerName: 'Units',
        field: field('units'),
        type: hplColumnType,
        sortable: true,
        filterParams: {
          filterOptions: NumberFilterOptionsWithAbsolute,
        },
      },
      {
        headerName: 'Description',
        field: field('description'),
        filterParams: {
          filterOptions: TextFilterOptionsWithContainsIn,
        },
      },
      {
        headerName: 'Execution Type',
        field: field('executionType'),
        filterParams: {
          filterOptions: TextFilterOptionsWithContainsIn,
        },
      },
      {
        ...this.getSetValuesFilterParams('securityTypeCodes'),
        headerName: 'Sec Type Code',
        field: field('securityTypeCode'),
      },
      {
        headerName: 'Accrued',
        field: field('accrued'),
        type: NfColumnTypes.Money,
        hide: true,
        filterParams: {
          filterOptions: NumberFilterOptionsWithAbsolute,
        },
      },
      {
        headerName: 'Commission',
        field: field('commission'),
        type: NfColumnTypes.Money,
        hide: true,
        filterParams: {
          filterOptions: NumberFilterOptionsWithAbsolute,
        },
      },
      {
        headerName: 'Loan Origination Fee',
        field: field('originationFee'),
        type: NfColumnTypes.Money,
        hide: true,
        sortable: true,
        filter: false,
      },
      {
        headerName: 'Loan Exit Fee',
        field: field('exitFee'),
        type: NfColumnTypes.Money,
        hide: true,
        sortable: true,
        filter: false,
      },
      {
        headerName: 'Loan Exit Fee Receivable',
        field: field('exitFeeReceivable'),
        type: NfColumnTypes.Money,
        hide: true,
        sortable: true,
        filter: false,
      },
      {
        headerName: 'Loan Draw Fee',
        field: field('drawFee'),
        type: NfColumnTypes.Money,
        hide: true,
        sortable: true,
        filter: false,
      },
      {
        headerName: 'Loan Prepayment Penalty Fee',
        field: field('prepaymentPenaltyFee'),
        type: NfColumnTypes.Money,
        hide: true,
        sortable: true,
        filter: false,
      },
      {
        headerName: 'Loan Delinquency Fee',
        field: field('delinquencyFee'),
        type: NfColumnTypes.Money,
        hide: true,
        sortable: true,
        filter: false,
      },
      {
        headerName: 'Loan Modification Fee',
        field: field('modificationFee'),
        type: NfColumnTypes.Money,
        hide: true,
        sortable: true,
        filter: false,
      },
      {
        headerName: 'Loan Miscellaneous Fee',
        field: field('miscellaneousFee'),
        type: NfColumnTypes.Money,
        hide: true,
        sortable: true,
        filter: false,
      },
      {
        headerName: 'Loan Prepaid Interest',
        field: field('prepaidInterest'),
        type: NfColumnTypes.Money,
        hide: true,
        sortable: true,
        filter: false,
      },
      {
        headerName: 'Loan Past Due Interest',
        field: field('interestDue'),
        type: NfColumnTypes.Money,
        hide: true,
        sortable: true,
        filter: false,
      },
      {
        headerName: 'Fee',
        field: field('fee'),
        type: NfColumnTypes.Money,
        hide: true,
        filterParams: {
          filterOptions: NumberFilterOptionsWithAbsolute,
        },
      },
      {
        ...this.getSetValuesFilterParams('currencyCodes'),
        headerName: 'Currency Code',
        field: field('currencyCode'),
      },
      {
        headerName: 'Ext Asset ID',
        field: field('externalSecIdentifier'),
        hide: true,
        filterParams: {
          filterOptions: TextFilterOptionsWithContainsIn,
        },
      },
      {
        headerName: 'External Cusip',
        field: field('externalCusip'),
        hide: true,
        filterParams: {
          filterOptions: TextFilterOptionsWithContainsIn,
        },
      },
      {
        headerName: 'Ext Alternate ID',
        field: field('externalSecAltIdentifier'),
        hide: true,
        filterParams: {
          filterOptions: TextFilterOptionsWithContainsIn,
        },
      },
      {
        headerName: 'Aggregate info',
        field: field('aggInfoString'),
        hide: true,
        filterParams: {
          filterOptions: TextFilterOptionsWithContainsIn,
        },
        tooltipValueGetter: params =>
          params.value?.replace(/,"/g, '\n').replace(/"/g, '') ?? '',
      },
      {
        headerName: 'Ext Transaction ID',
        field: field('extTranId'),
        sortable: true,
        type: NfColumnTypes.Identifier,
        filterParams: {
          filterOptions: NumberFilterOptionsWithoutEquals,
        },
      },
      {
        headerName: 'Execution ID',
        field: field('executionId'),
        sortable: true,
        type: NfColumnTypes.Identifier,
        filterParams: {
          filterOptions: NumberFilterOptionsWithoutEquals,
        },
      },
      {
        ...this.getSetValuesFilterParams('dataSourceType'),
        headerName: 'Data Source Type',
        field: field('dataSourceType'),
      },
      {
        headerName: 'Security ID',
        field: field('securityId'),
        type: NfColumnTypes.Identifier,
        hide: true,
        sortable: true,
        filterParams: {
          filterOptions: NumberFilterOptionsWithoutEquals,
        },
      },
      {
        field: field('basis'),
        headerName: 'Basis',
        hide: true,
      },
      {
        headerName: 'Recon Date',
        field: field('reconDate'),
        type: NfColumnTypes.Date,
        hide: true,
        valueFormatter: params => {
          return formatDates(params.value, 'yyyy-MM-dd', 'MM/dd/yy')
        },
        tooltipValueGetter: params => {
          return formatDates(params.value, 'yyyy-MM-dd', 'MM/dd/yyyy')
        },
      },
      {
        ...this.getSetValuesFilterParams('statusChangedBy'),
        headerName: 'Status Changed By',
        field: field('statusChangedBy'),
        hide: true,
      },
      {
        headerName: 'Sell Units',
        field: field('fromUnits'),
        type: hplColumnType,
        hide: true,
        filterParams: {
          filterOptions: NumberFilterOptionsWithAbsolute,
        },
      },
      {
        headerName: 'From Amount',
        field: field('fromAmount'),
        type: hplColumnType,
        hide: true,
        filterParams: {
          filterOptions: NumberFilterOptionsWithAbsolute,
        },
      },
      {
        headerName: 'FX Rate',
        field: field('exchangeRate'),
        type: NfColumnTypes.Number,
        hide: true,
        filterParams: {
          filterOptions: NumberFilterOptionsWithAbsolute,
        },
      },
      {
        headerName: 'Type Label',
        field: field('typeLabel'),
        hide: true,
        filterParams: {
          filterOptions: TextFilterOptionsWithContainsIn,
        },
      },
      {
        ...this.getSetValuesFilterParams('accountId'),
        headerName: 'Account ID',
        field: field('accountId'),
        hide: true,
        sortable: true,
        type: NfColumnTypes.Identifier,
      },
      {
        ...this.getSetValuesFilterParams('accountName'),
        headerName: 'Account Name',
        field: field('accountName'),
        sortable: true,
      },
      {
        headerName: 'Contract ID',
        field: field('contractIdentifier'),
        hide: true,
        filterParams: {
          filterOptions: TextFilterOptionsWithContainsIn,
        },
      },
      {
        headerName: 'Data Source',
        field: field('dataSource'),
        filterParams: {
          filterOptions: TextFilterOptionsWithContainsIn,
        },
      },
      {
        headerName: 'External Sec Desc',
        field: field('externalSecDesc'),
        hide: true,
        filterParams: {
          filterOptions: TextFilterOptionsWithContainsIn,
        },
      },
      {
        headerName: 'Ext Alternate ID Description',
        field: field('externalSecAltIdDesc'),
        hide: true,
      },
      {
        headerName: 'Factor',
        field: field('factor'),
        hide: true,
        filter: false,
      },
      {
        headerName: 'Prometheus Transaction ID',
        field: field('prometheusTranId'),
        hide: true,
        sortable: true,
        type: NfColumnTypes.Identifier,
        width: heliosDefaultColWidth.Id,
        filterParams: {
          filterOptions: NumberFilterOptionsWithoutEquals,
        },
      },
      {
        headerName: 'CW Transaction IDs',
        field: field('cwTranIds'),
        filterParams: {
          filterOptions: ['in', 'notIn'],
        },
        hide: true,
        type: NfColumnTypes.Identifier,
        sortable: true,
      },
      {
        ...this.getSetValuesFilterParams('collateralBroker'),
        headerName: 'Collateral Broker',
        field: field('collateralBroker'),
        hide: true,
        sortable: true,
      },
      {
        headerName: 'Capitalized',
        field: field('isCapitalize'),
        type: HeliosColumnTypes.Boolean,
        hide: true,
        sortable: true,
      },
      {
        headerName: 'ELM Client Transaction ID',
        field: field('custodyLotIdentifier'),
        hide: true,
        filterParams: {
          filterOptions: TextFilterOptionsWithContainsIn,
        },
      },
    ]

    const savedViewExists = await this.store
      .pipe(
        selectSavedViewExistsForGridKey(ExternalTransactionsMainGridKey),
        takeUntil(this.destroySubject$),
        take(1),
      )
      .toPromise()

    if (!savedViewExists) {
      return baseColDefs
    }

    const savedColumnState = await this.store
      .pipe(
        takeUntil(this.destroySubject$),
        selectActiveSavedViewForGrid(ExternalTransactionsMainGridKey),
        take(1),
        map(savedView => savedView.savedView.columnState),
      )
      .toPromise()

    let colDefs = mergeBaseColDefsWithColumnState(baseColDefs, savedColumnState)

    const availableColumns = await this.availableRestrictedColumns
    if (availableColumns) {
      const columnsToHide = availableColumns
        .filter(column => !column.isAvailable)
        .map(column => column.column)

      colDefs = colDefs.map(colDef => {
        if (colDef.colId && columnsToHide.includes(colDef.colId)) {
          return {
            ...colDef,
            hide: true,
            suppressMoveable: true,
            suppressMenu: true,
            suppressColumnsToolPanel: true,
          }
        }
        return colDef
      })
    }

    return colDefs
  }

  private createDatasource(): IServerSideDatasource {
    return {
      getRows: async (params: IServerSideGetRowsParams) => {
        try {
          this.filterCount = countFilters(params.api, null) ?? 0

          this.gridState = getGridState(this.gridApi)

          const request = toHeliosRequest<ExternalTransaction>(
            params,
            [],
            externalTransactionCommentary.mandatoryFields(
              'accountName',
              'rowId',
              'executionId',
              'dataSourceType',
              'securityId',
              'originId',
              'cwTranIds',
              'reconDate',
              'currencyCode',
              'amountByCurrencyCode',
              'predictedAction',
              'predictedActionProbability',
              'externalSecIdentifier',
            ),
            undefined,
            this.sortByAbsoluteValueToggle.value,
          )

          //override predictedActionProbability with predictedAction to filter the Predicted Action column
          if (request.filter.predictedActionProbability) {
            const predictedActionKey = request.filter.predictedActionProbability
            delete request.filter.predictedActionProbability
            request.filter.predictedAction = predictedActionKey
          }

          const extTrans = await this.transactionsService.getTransactions(
            request,
            this.cacheLoaded$,
          )

          const totalRows = (params.request.startRow || 0) + extTrans.length

          params.success({
            rowData: extTrans,
            rowCount:
              totalRows < (params?.request?.endRow ?? totalRows)
                ? totalRows
                : undefined,
          })
        } catch (e) {
          params.fail()
        }

        this.resetSetValues()
      },
    }
  }

  private onRowDoubleClicked(event: RowDoubleClickedEvent) {
    const isLIBTran = this.isLIBTransaction(event.data)
    if (isLIBTran) return

    this.createTransactionsModalService.toggleCreateTransactionModal(
      event.data.tranTypeCode,
      {
        modalMode: TransactionModalMode.CreateFromExtTran,
        transactionId: event.data.extTranId.toString(),
        origTransactionId: event.data.legacyOrigId,
        externalTransactionStatus:
          ExternalTransactionStatus[
            event.data.status as ExternalTransactionStatusKey
          ],
      },
    )
  }

  isLIBTransaction(data?: ExternalTransaction) {
    return ['TRNI', 'TRNO', 'LOAD'].includes(data?.tranTypeCode ?? '') &&
      data?.basis &&
      this.sessionBasisName
      ? true
      : false
  }

  getSetValuesFilterParams(key: keyof ExternalTransactionSetValues) {
    return {
      field: field('predictedAction'),
      filter: 'agSetColumnFilter',
      filterParams: {
        defaultToNothingSelected: true,
        refreshValuesOnOpen: true,
        values: params => {
          this.externalTransactionSetValues$.subscribe(values =>
            params.success((values[key] as string[]) ?? []),
          )
        },
        closeOnApply: true,
        buttons: ['apply', 'reset'],
      } as ISetFilterParams,
    }
  }
  //#endregion GridConfig
  /**
   * Quick filter search for asset ID
   */
  updateQuickSearchAssetIds(assetIdTextSearch?: boolean) {
    if (this.quickSearchAssetIds.invalid) {
      return
    }

    const isAssetIdNullOrEmpty = isNullOrEmpty(this.quickSearchAssetIds.value)

    const assetIdsLength =
      this.quickSearchAssetIds.value?.split(',').length ?? 0

    const filter = {
      type: FilterTypeForQuickFilterOfLength(assetIdsLength),
      filterType: 'text',
      filter: this.quickSearchAssetIds.value,
    }

    if (!isAssetIdNullOrEmpty) {
      this.setFilter('secIdentifier', filter)
    } else if (assetIdTextSearch) {
      this.setFilter('secIdentifier', undefined)
    }
  }

  setStatusFilter(statusFilterSet: Set<string>) {
    if (!this.gridApi) {
      return
    }

    const filter = {
      filterType: 'set',
      values: Array.from(statusFilterSet),
    }

    this.setFilter('status', filter)
    this.setStatusFormControls(statusFilterSet)
  }

  updateGridConfig() {
    const panelSettingsChangedEvent = new CustomEvent('panelSettingsChanged', {
      detail: {
        api: this.gridApi,
        panelSettings: this.getCurrentPanelSettingsObject(),
      } as WorkspaceUpdateEventObject,
    })
    this.gridApi?.api.dispatchEvent(panelSettingsChangedEvent)
  }

  getCurrentPanelSettingsObject() {
    const panelSettings: { [key: string]: unknown } = {}
    panelSettings['sortByAbsolute'] = this.sortByAbsoluteValueToggle.value
    panelSettings['clearFiltersOnAccountLoad'] =
      this.clearFiltersOnAccountLoadToggle.value
    return panelSettings
  }

  private applyGridConfig(agGridStateSaved: AgGridState) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const panelSettings = agGridStateSaved.panelSettings as any

    if (panelSettings) {
      if ('sortByAbsolute' in panelSettings)
        this.sortByAbsoluteValueToggle.patchValue(
          Boolean(panelSettings.sortByAbsolute),
          { emitEvent: false },
        )

      if ('clearFiltersOnAccountLoad' in panelSettings)
        this.clearFiltersOnAccountLoadToggle.patchValue(
          Boolean(panelSettings.clearFiltersOnAccountLoad),
          { emitEvent: false },
        )
    }
  }

  resetSetValues() {
    this.externalTransactionSetValues$ = this.getExternalTransactionSetValues()
  }

  private getExternalTransactionSetValues() {
    return this.transactionsService
      .getExternalTransactionSetValues()
      .pipe(take(1), shareReplay(1))
  }

  private async updateAvailableColumns(columnApi: ColumnApi): Promise<void> {
    const columns = await this.availableRestrictedColumns
    const columnsToHide = columns
      .filter(column => !column.isAvailable)
      .map(column => column.column)

    const updatedColumns = columnsToHide.map(column => {
      return {
        colId: column,
        hide: true,
      }
    })

    columnApi.applyColumnState({
      state: updatedColumns,
      applyOrder: true,
    })
  }

  private async onGridReady(event: GridReadyEvent) {
    this.gridApi = event

    wireCommentaryCounts<ExternalTransaction>({
      changes$: this.commentaryCountsService.changes$,
      getApi: () => event.api,
      hasAccessForLoad: () => this.hasAccessCommentary,
      destroy$: this.destroySubject$,
      accountIdOf: row => row?.accountId,
      loadCounts: ids =>
        this.commentaryCountsService.ensureEntityCountsLoaded(ids),
      live: { accounts: this.commentaryLiveAccounts, surface: 'entity' },
    })
    refilterOnThreadChanges<ExternalTransaction>(
      () => event.api,
      this.destroySubject$,
      {
        accountIdOf: row => row?.accountId,
        queriedAccountIds: activeSessionAccountIds(
          this.store,
          this.destroySubject$,
        ),
      },
    )

    enableHeliosMultiRowDragSelection(event.api)
    enableHeliosCopyBehavior(event.api)

    this.preferences
      .getDensityLevel()
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(level =>
        enableHeliosGridDensityLevel(event.api, DensityLevel.Compact === level),
      )

    this.filterModalService.registerGrid(
      event.api,
      ExternalTransactionsKey,
      ExternalTransactionsMainGridKey,
      [COMMENTARY_COLUMN_ID],
    )

    this.filterModalService.onResetFilters
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(() => {
        this.resetAllFilters()
      })
    if (this.statusFilterSet$) {
      this.statusFilterSet$
        .pipe(
          distinctUntilChanged((a: Set<string>, b: Set<string>): boolean =>
            compareSets(a, b),
          ),
          takeUntil(this.destroySubject$),
        )
        .subscribe(statusFilterSet => {
          this.setStatusFilter(statusFilterSet)
        })
    }

    /* why move this later ? so that all filter change events are called before workspace is rendered and only if workspace
    has filter model it ever again calls filter model change not otherwise avoiding unnecessary race conditions */
    const agGridStateSaved = await this.savedViewService.manageSavedView(
      event,
      ExternalTransactionsMainGridKey,
      this.destroySubject$,
      true,
      this.getCurrentPanelSettingsObject(),
    )

    this.applyGridConfig(agGridStateSaved)
    if (this.clearFiltersOnAccountLoadToggle.value) {
      this.setStatusFilter(new Set<string>(STATUS_FILTER_DEFAULTS))
    }
    await this.updateAvailableColumns(event.columnApi)
    refreshSetValuesForAllSetFilterColumns(event)
  }

  /** @internal */
  setFilter(
    columnName: Extract<keyof ExternalTransaction, string>,
    filter: unknown,
  ): void {
    setFilter<ExternalTransaction>(columnName, filter, this.gridApi?.api)
  }

  onFilterChanged(params: FilterChangedEvent) {
    this.onStatusFilterChanged(params)
    this.onAssetIdFilterChanged(params)
    this.updateResetFiltersEnabledButton(params.api)
  }

  private updateResetFiltersEnabledButton(api: GridApi) {
    const filterModels = api.getFilterModel() ?? {}
    const activeFilters = Object.keys(filterModels).filter(k => k !== 'status')
    const isStatusFilterDefaultState =
      filterModels?.status?.values.length === STATUS_FILTER_DEFAULTS.length &&
      filterModels?.status?.values.every((v: string) =>
        STATUS_FILTER_DEFAULTS.includes(v),
      )
    const isSetToDefaults = isStatusFilterDefaultState

    this.resetAllFiltersEnabled = activeFilters.length > 0 || !isSetToDefaults
  }

  private onAssetIdFilterChanged(params: FilterChangedEvent) {
    const filterModel = params.api.getFilterModel()
    const assetIdValue = filterModel?.secIdentifier?.filter
    const filterType = filterModel?.secIdentifier?.type

    const assetIdsLength = assetIdValue?.split(',').length ?? 0
    if (filterType === FilterTypeForQuickFilterOfLength(assetIdsLength)) {
      this.quickSearchAssetIds.setValue(assetIdValue)
    } else {
      this.quickSearchAssetIds.reset('', { emitEvent: false })
    }
  }

  private onStatusFilterChanged(params: FilterChangedEvent) {
    const columnsChanged = params.columns ?? []
    const hasStatusFilterChanged = columnsChanged
      .map(col => col.getColId())
      .includes('status')
    if (!hasStatusFilterChanged) {
      return
    }

    const existingFilters =
      (params.api?.getFilterModel() as {
        [key: string]: { values: string[] }
      }) ?? {}
    const existingStatusFilterSet = new Set(
      existingFilters?.status?.values ?? [],
    )
    this.setStatusFormControls(existingStatusFilterSet)
  }

  private setStatusFormControls(statuses: Set<string>) {
    this.includeMatchedFormControl.setValue(statuses.has('MATCHED'))
    this.includeUnmatchedFormControl.setValue(statuses.has('UNMATCHED'))
    this.includeIgnoredFormControl.setValue(statuses.has('IGNORED'))
    this.includeHeldFormControl.setValue(statuses.has('HELD'))
  }

  resetAllFilters() {
    this.quickSearchAssetIds.reset('')
    this.updateQuickSearchAssetIds()
    this.gridApi?.api.setFilterModel(null)
    this.resetStatusFilter()
    this.gridApi?.api.onFilterChanged()
  }

  resetStatusFilter() {
    this.setStatusFilter(new Set<string>(STATUS_FILTER_DEFAULTS))
  }

  /**
   * Exports the transactions to Excel same as visible in the Ext table
   */
  exportExtTranToExcel() {
    const tableFilters = this.gridApi?.api.getFilterModel()
    let filters
    if (tableFilters) filters = convertFilters(tableFilters)
    const fields: string[] = []
    const colHeaderNames: string[] = []
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone

    this.gridApi?.columnApi.getAllDisplayedColumns().forEach(col => {
      const colField = col.getColId()
      if (colField === COMMENTARY_COLUMN_ID) return
      fields.push(colField)
      const headerName = col.getUserProvidedColDef()?.headerName
      if (headerName) colHeaderNames.push(headerName)
    })

    const request: ExportExcelRequest = {
      fields: fields,
      headers: colHeaderNames,
      filter: filters as FilterModel,
      userTimeZone: userTimeZone,
    }

    this.transactionsService.exportToExcel(request).subscribe(
      response => {
        downloadExcelFile(response, 'External Transactions')
        this.toastService.success(
          "Export to Excel completed successfully. The exported file is available in your browser's Downloads",
          {
            closeButton: true,
            timeOut: 3000,
          },
        )
      },
      () => {
        this.toastService.error(
          'Export to Excel failed. Please try again.',
          '',
          {
            closeButton: true,
            timeOut: 3000,
            disableTimeOut: false,
          },
        )
      },
    )
    this.toastService.warning(
      'Export to Excel is in progress. It may take more than 30 seconds. Please wait.',
      {
        closeButton: true,
        timeOut: 3000,
      },
      'In Progress',
    )
  }

  launchFiltersModal() {
    this.filterModalService.launchFiltersModal()
  }

  deduceRowClass(params: RowClassParams): string {
    let rowClass = this.highlightService.getRowClass(params.node.id ?? '')
    if (!rowClass) {
      rowClass = this.getDirectMatchRowClass(
        params.node.data?.extTranId ?? -1,
        params.node,
      )
    }
    return rowClass
  }
  getDirectMatchRowClass(extTranId: number, rowNode: RowNode): string {
    const shouldAddDirectMatch =
      !this.newSelectionRefresh &&
      this.cwTransSelected.find(cwTrans =>
        cwTrans.extTranIds?.includes(extTranId),
      )
    if (shouldAddDirectMatch) {
      this.directMatchedTransactions.add(rowNode)
      return 'direct-match'
    } else {
      return ''
    }
  }
  private registerHotkeys() {
    const ordinals = ['1st', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th']
    for (let i = 0; i < ordinals.length; i++) {
      this.hotkeyDestroyStack.push(
        this.hotkeysService.bind(
          `highlight-row-with-${ordinals[i]}-color-in-ext-transactions` as HighlightRowsInExtTransactions,
          () =>
            this.highlightService.setHighlightOnExternalAction(this.gridApi, i),
        ),
        this.hotkeysService.bind('select-all-ext-trans', () => {
          this.userIsSelectingAllRows = true
        }),
      )
    }
  }
  private unregisterHotkeys() {
    this.hotkeyDestroyStack.forEach(hotkeyDestroyFn => hotkeyDestroyFn())
    this.hotkeyDestroyStack.length = 0
  }

  toggleSelectAllExcludedTranRows(extTranId: number) {
    if (this.selectAllExcludedTranRows.has(extTranId))
      this.selectAllExcludedTranRows.delete(extTranId)
    else this.selectAllExcludedTranRows.add(extTranId)
  }

  private getSelectAllRequest(
    api: GridApi,
  ): HeliosServerSideRequest<ExternalTransaction> {
    const appliedFilters = convertFilters(api.getFilterModel() ?? {})
    const selectAllFilters: Record<string, Filter> = {
      ...appliedFilters,
      extTranId: {
        operator: 'AND',
        filterType: 'number',
        filters: [
          {
            operator: 'notIn',
            values: Array.from(this.selectAllExcludedTranRows.values()),
          },
        ],
      } as Filter,
    }

    return {
      ...genericHeliosRequest,
      filter: selectAllFilters,
    }
  }

  /**
   * @internal
   */
  async updateSelectAllData(api: GridApi) {
    const summary = await this.transactionsService.getTransactionSummaries(
      this.getSelectAllRequest(api),
    )
    this.transactionsDataSummary.next(summary)
    api.dispatchEvent({
      type: Events.EVENT_SELECTION_CHANGED,
    })

    this.selectAllTransactionsData =
      await this.transactionsService.getTransactions(
        {
          ...this.getSelectAllRequest(api),
          startRow: 0,
          endRow: summary.rowCount ?? 0,
          fields: ['rowId', 'extTranId', 'status', 'dataSourceType'],
        },
        this.cacheLoaded$,
      )
  }

  private async getAvailableRestrictedColumns(): Promise<
    TransactionAvailableRestrictedColumns[]
  > {
    return this.transactionsService
      .getAvailableRestrictedColumns()
      .pipe(
        takeUntil(this.destroySubject$),
        map(columns =>
          columns.map(column => {
            if (column.column === 'metaData') {
              return { column: 'metaData_8', isAvailable: column.isAvailable }
            } else return column
          }),
        ),
      )
      .toPromise()
  }
}
